/**
 * Klient pro autentizaci proti tickets-portal API (https://prace.sitepark.cz).
 *
 * Tickets-portal endpointy:
 *   POST /api/auth/login    { email, password } → { user, accessToken } + httpOnly refresh cookie
 *   POST /api/auth/refresh                       → { user, accessToken, error } z httpOnly cookie
 *   POST /api/auth/logout                        → smaže refresh cookie
 *
 * Cross-origin pozn.: invoice frontend běží na jiné doméně/portu/protokolu než
 * tickets-portal, takže fetch musí být s `credentials: "include"`. Tickets-portal
 * API musí mít CORS s povoleným origin invoice frontendu (tj. např.
 * `Access-Control-Allow-Origin: http://node1.sitepark.cz:11000` +
 * `Access-Control-Allow-Credentials: true`). Pokud refresh cookie nedorazí
 * (např. kvůli SameSite/Secure flagsu nebo HTTP vs HTTPS), refresh prostě
 * vrátí `{ user: null }` a uživatel se přihlásí znova.
 */

/**
 * Default je prázdný string → relative URL → same-origin (`/api/auth/login`).
 * Nginx na invoices.sitepark.cz to interně proxnuje na prace.sitepark.cz,
 * takže odpadá cross-origin a CORS.
 *
 * Pokud chceš v dev volat tickets-portal přímo (bez proxy), nastav
 * VITE_AUTH_API_URL=https://prace.sitepark.cz.
 */
export const AUTH_API_URL: string =
  (import.meta.env.VITE_AUTH_API_URL as string | undefined) ?? "";

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

export interface LoginResponse {
  user: AuthUser;
  accessToken: string;
}

export interface RefreshResponse {
  user: AuthUser | null;
  accessToken: string | null;
  error: string | null;
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

export async function loginRequest(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const res = await fetch(`${AUTH_API_URL}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new AuthApiError(
      body?.error ?? `Login failed (HTTP ${res.status})`,
      res.status,
    );
  }
  return (await res.json()) as LoginResponse;
}

export async function refreshRequest(): Promise<RefreshResponse> {
  try {
    const res = await fetch(`${AUTH_API_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return { user: null, accessToken: null, error: `http_${res.status}` };
    }
    return (await res.json()) as RefreshResponse;
  } catch {
    return { user: null, accessToken: null, error: "network" };
  }
}

export async function logoutRequest(): Promise<void> {
  try {
    await fetch(`${AUTH_API_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // ignore — pro lokální cleanup state stačí
  }
}
